import { memo, type JSX } from 'react';
import timerOrange from '@/assets/svg/static/timerOrange.svg';
import { useCountdown } from '@/hooks/useCountdown';
import type { BrandOffer } from '@/types/Product.types';
import { formatIndianPrice } from '@/utils/formatting';
import {
  MS_PER_DAY,
  ONE_DAY_SECONDS,
  TEXT_CURRENCY_SYMBOL,
  TEXT_DAY_LEFT_SUFFIX,
  TEXT_DAYS_LEFT_SUFFIX,
  TEXT_GET_AT,
  TEXT_SEPARATOR,
} from './ProductOfferStrip.constants';
import styles from './ProductOfferStrip.module.css';
import type { ProductOfferStripProps } from './ProductOfferStrip.types';

const shouldShowTimer = (
  brandOffer: BrandOffer,
  timeRemaining: number | null
): boolean => {
  if (brandOffer.showTimer === true) return true;
  if (timeRemaining === null) return false;
  return timeRemaining > 0 && timeRemaining < ONE_DAY_SECONDS;
};

const isOfferExpired = (
  brandOffer: BrandOffer,
  timeRemaining: number | null
): boolean =>
  !brandOffer.showTimer && timeRemaining !== null && timeRemaining <= 0;

const computeDaysLeft = (endsAt: string | null): number => {
  if (!endsAt) return 0;
  const target = new Date(endsAt).getTime();
  if (Number.isNaN(target)) return 0;
  return Math.max(0, Math.ceil((target - Date.now()) / MS_PER_DAY));
};

const formatDaysLeft = (endsAt: string | null): string => {
  if (!endsAt) return '';
  const days = computeDaysLeft(endsAt);
  return `${days}${days === 1 ? TEXT_DAY_LEFT_SUFFIX : TEXT_DAYS_LEFT_SUFFIX}`;
};

const renderPriceBlock = (brandOffer: BrandOffer): JSX.Element => {
  return (
    <span className={styles.pricePdp}>
      <span className={styles.getAtPdp}>{TEXT_GET_AT}</span>
      {TEXT_CURRENCY_SYMBOL}
      {formatIndianPrice(String(brandOffer.discountOfferPrice))}
    </span>
  );
};

const renderTimerBlock = (formatted: string): JSX.Element => {
  return (
    <div className={styles.rightBlock}>
      <img src={timerOrange} className={styles.timerIcon} alt="Timer Icon" />
      <span className={`${styles.orangeTextBase} ${styles.orangeTextPdp}`}>
        {formatted}
      </span>
    </div>
  );
};

const renderEverydayCoupon = (brandOffer: BrandOffer): JSX.Element => (
  <span
    className={`${styles.orangeTextBase} ${styles.orangeTextPdp}`}
    title={brandOffer.offerText}
  >
    {brandOffer.offerText}
  </span>
);

const renderSale = (
  brandOffer: BrandOffer,
  formatted: string | null,
  timeRemaining: number | null
): JSX.Element | null => {
  const showTimer = shouldShowTimer(brandOffer, timeRemaining);
  if (isOfferExpired(brandOffer, timeRemaining)) return null;
  if (showTimer && !formatted) return null;

  const trailing = showTimer ? formatted : formatDaysLeft(brandOffer.endsAt);
  const prefix = brandOffer.name ? `${brandOffer.name}${TEXT_SEPARATOR}` : '';

  return (
    <span
      className={`${styles.orangeTextBase} ${styles.orangeTextPdp}`}
      title={`${prefix}${trailing}`}
    >
      {prefix}
      {trailing}
    </span>
  );
};

const renderSaleExtraOffers = (
  brandOffer: BrandOffer,
  formatted: string | null,
  timeRemaining: number | null
): JSX.Element | null => {
  if (isOfferExpired(brandOffer, timeRemaining)) return null;

  const hasPrice = brandOffer.discountOfferPrice > 0;
  const showTimer =
    timeRemaining !== null &&
    timeRemaining > 0 &&
    timeRemaining < ONE_DAY_SECONDS;

  return (
    <>
      {hasPrice ? renderPriceBlock(brandOffer) : null}
      {showTimer && formatted ? (
        renderTimerBlock(formatted)
      ) : (
        <div className={styles.textWrapper}>
          <span className={`${styles.orangeTextBase} ${styles.orangeTextPdp}`}>
            {formatDaysLeft(brandOffer.endsAt)}
          </span>
        </div>
      )}
    </>
  );
};

const renderCoupons = (
  brandOffer: BrandOffer,
  timeRemaining: number | null
): JSX.Element | null => {
  if (isOfferExpired(brandOffer, timeRemaining)) return null;
  if (brandOffer.discountOfferPrice <= 0) return null;

  return (
    <>
      {renderPriceBlock(brandOffer)}
      <div className={styles.textWrapper}>
        <span
          className={`${styles.orangeTextBase} ${styles.orangeTextPdp}`}
          title={brandOffer.offerText}
        >
          {brandOffer.offerText}
        </span>
      </div>
    </>
  );
};

const ProductOfferStrip = ({
  brandOffer,
  pricingOffer,
}: ProductOfferStripProps): JSX.Element | null => {
  // Brand Offer logic hook
  const brandCountdown = useCountdown(brandOffer?.endsAt ?? null);

  // Pricing Offer logic hook
  const pricingCountdown = useCountdown(pricingOffer?.expiresAt ?? null);

  if (brandOffer) {
    let content: JSX.Element | null = null;
    switch (brandOffer.offerType) {
      case 'EVERYDAY_COUPONS':
        content = renderEverydayCoupon(brandOffer);
        break;
      case 'SALE':
        content = renderSale(
          brandOffer,
          brandCountdown.formatted,
          brandCountdown.timeRemaining
        );
        break;
      case 'SALE_EXTRA_OFFERS':
        content = renderSaleExtraOffers(
          brandOffer,
          brandCountdown.formatted,
          brandCountdown.timeRemaining
        );
        break;
      case 'COUPONS':
      case 'TIERED_COUPONS':
        content = renderCoupons(brandOffer, brandCountdown.timeRemaining);
        break;
      default:
        return null;
    }

    if (content === null) return null;

    return (
      <div className={`${styles.offerStripBase} ${styles.offerStripPdp}`}>
        <div className={`${styles.innerContainer} ${styles.innerPdp}`}>
          {content}
        </div>
      </div>
    );
  }

  if (pricingOffer) {
    const timeRemaining = pricingCountdown.timeRemaining;
    const formatted = pricingCountdown.formatted;

    const isExpired =
      pricingOffer.expiresAt !== null &&
      timeRemaining !== null &&
      timeRemaining <= 0;

    if (isExpired) return null;

    const hasPrice = pricingOffer.price > 0;
    const showTimer =
      pricingOffer.expiresAt !== null &&
      timeRemaining !== null &&
      timeRemaining > 0;

    const fallbackLabel =
      pricingOffer.type === 'FIRST_ORDER_OFFER'
        ? '1st order offer'
        : 'Special Offer';

    const labelToShow = pricingOffer.label || fallbackLabel;

    return (
      <div className={`${styles.offerStripBase} ${styles.offerStripPdp}`}>
        <div className={`${styles.innerContainer} ${styles.innerPdp}`}>
          {hasPrice && (
            <span className={styles.pricePdp}>
              <span className={styles.getAtPdp}>{TEXT_GET_AT}</span>
              {TEXT_CURRENCY_SYMBOL}
              {formatIndianPrice(String(pricingOffer.price))}
            </span>
          )}
          {showTimer && formatted ? (
            <div className={styles.rightBlock}>
              <img
                src={timerOrange}
                className={styles.timerIcon}
                alt="Timer Icon"
              />
              <span
                className={`${styles.orangeTextBase} ${styles.orangeTextPdp}`}
              >
                {formatted}
              </span>
            </div>
          ) : (
            <div className={styles.textWrapper}>
              <span
                className={`${styles.orangeTextBase} ${styles.orangeTextPdp}`}
                title={labelToShow}
              >
                {labelToShow}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default memo(ProductOfferStrip);
